import { supabase } from "@/integrations/supabase/client";
import { UserRole } from "@/types";

// Fetch all user roles
export const fetchUserRoles = async (): Promise<UserRole[]> => {
  const { data, error } = await supabase
    .from("user_roles")
    .select("*")
    .order("id");

  if (error) {
    console.error("Error fetching user roles:", error);
    throw error;
  }

  // Transform from Supabase format to our app format
  return data.map((role) => ({
    id: role.id,
    name: role.name,
    description: role.description,
    isActive: role.is_active,
    isSystem: role.is_system,
  }));
};

// Fetch a single user role by ID
export const fetchUserRole = async (id: number): Promise<UserRole | null> => {
  const { data, error } = await supabase
    .from("user_roles")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null;
    }
    console.error("Error fetching user role:", error);
    throw error;
  }

  // Transform from Supabase format to our app format
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    isActive: data.is_active,
    isSystem: data.is_system,
  };
};

// Create a new user role
export const createUserRole = async (
  name: string,
  description?: string
): Promise<UserRole> => {
  // Get the next available ID
  const { data: maxIdData } = await supabase
    .from("user_roles")
    .select("id")
    .order("id", { ascending: false })
    .limit(1)
    .single();

  const nextId = maxIdData ? maxIdData.id + 1 : 1;

  const { data, error } = await supabase
    .from("user_roles")
    .insert({
      id: nextId,
      name,
      description,
      is_active: true,
      is_system: false,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating user role:", error);
    throw error;
  }

  // Transform from Supabase format to our app format
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    isActive: data.is_active,
    isSystem: data.is_system,
  };
};

// Update an existing user role
export const updateUserRole = async (
  id: number,
  updates: Partial<UserRole>
): Promise<UserRole> => {
  // Convert from our format to Supabase format
  const supabaseUpdates: Record<string, unknown> = {};
  if (updates.name !== undefined) supabaseUpdates.name = updates.name;
  if (updates.description !== undefined)
    supabaseUpdates.description = updates.description;
  if (updates.isActive !== undefined)
    supabaseUpdates.is_active = updates.isActive;

  const { data, error } = await supabase
    .from("user_roles")
    .update(supabaseUpdates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating user role:", error);
    throw error;
  }

  // Transform from Supabase format to our app format
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    isActive: data.is_active,
    isSystem: data.is_system,
  };
};

// Delete a user role
export const deleteUserRole = async (id: number): Promise<void> => {
  const { error } = await supabase.from("user_roles").delete().eq("id", id);

  if (error) {
    console.error("Error deleting user role:", error);
    throw error;
  }
};

// Get the count of users assigned to a specific role
export const getUserCountForRole = async (roleId: number): Promise<number> => {
  const { count, error } = await supabase
    .from("user_to_roles")
    .select("*", { count: "exact", head: true })
    .eq("role_id", roleId);

  if (error) {
    console.error("Error getting user count for role:", error);
    throw error;
  }

  return count || 0;
};
